
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Video from 'plaid-threads/Icons/Video';
import Libraries from 'plaid-threads/Icons/Libraries';
import Message from 'plaid-threads/Icons/Message';
import Layout from '../../../components/docs/Layout';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
import External from 'plaid-threads/Icons/External';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Onboard users instantly with just a phone number'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Layer'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Plaid Layer'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/layer/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='https://github.com/plaid/layer-quickstart' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/6717daecf618361b93d583f5?zoom=100' />
  <Actions.SmallCard icon={<Message mdxType="Message" />} title='Request Access' link='https://plaid.com/layer/#contact-form' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Instantly onboard millions of people from the Plaid Network with Layer (US only).`}</p>
    <p>{`The Plaid Network is made up of more than 100 million consumers who have chosen to be `}<a parentName="p" {...{
        "href": "/docs/link/returning-user/"
      }}>{`remembered by Plaid`}</a>{`, each with a saved profile holding their identity data and the financial accounts they have connected. Layer draws on those profiles. How many of your users are eligible depends on the requirements you set in your Layer template, so the eligible share of your traffic is a subset of the network rather than a fixed number; your Plaid account manager can estimate it for your specific criteria.`}</p>
    <p>{`When a user provides their phone number, Plaid Layer checks whether they are part of the Plaid Network and verifies that they meet your onboarding requirements.
Plaid Layer then authenticates the device and performs real-time risk analysis to reduce the risk of fraudulent sign-up attempts.
After the user confirms their details and decides which financial account they want to share, you can verify their identity using your KYC provider and connect their accounts with Plaid.`}</p>
    <ProductTour steps={[{
      text: 'The user enters their phone number within your application.',
      image: '/assets/img/docs/layer/layer_enter_phone.png'
    }, {
      text: 'Plaid determines the user is eligible for Layer, so they are prompted to continue with Plaid.',
      image: '/assets/img/docs/layer/layer_intro.png'
    }, {
      text: "The user's device is authenticated...",
      image: '/assets/img/docs/layer/layer_auth.png'
    }, {
      text: '...and the user chooses what information to share with your application.',
      image: '/assets/img/docs/layer/layer_share.png'
    }, {
      text: "And we're done! You're now able to retrieve user account data.",
      image: '/assets/img/docs/layer/layer_done.png'
    }]} mdxType="ProductTour" />
    <p>{`To get started with Layer, `}<a parentName="p" {...{
        "href": "https://plaid.com/layer/#contact-form"
      }}>{`contact sales`}</a>{` to request Sandbox access.`}</p>
    <Callout icon={<External mdxType="External" />} mdxType="Callout">
      <p><a parentName="p" {...{
          "href": "https://plaid.coastdemo.com/share/6717daecf618361b93d583f5?zoom=100"
        }}>{`See a live, interactive demo`}</a>{` of a Plaid-powered onboarding flow using Layer.`}</p>
    </Callout>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <Callout mdxType="Callout">
      <p>{`  If you use Layer with Consumer Report or other Plaid Check Products, see the `}<a parentName="p" {...{
          "href": "/docs/check/onboard-users-with-layer/"
        }}>{`Plaid Check / Layer onboarding guide`}</a>{`.`}</p>
    </Callout>
    <YouTubeEmbed videoId='vMo6VyJSWeg' alignment='full' title='Plaid Layer in 3 minutes' caption='Prefer to learn by watching? Check out this quick video guide to implementing Layer with Extended Autofill' mdxType="YouTubeEmbed" />
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Configure a Layer template via the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/layer"
          }}>{`Dashboard`}</a>{`. A template is created by default on first visit. You can customize the template's name, set logo and colors to match your brand, and configure eligibility requirements. In general, fill rates for different user data identity fields are highly correlated with each other (i.e., if Plaid has access to one piece of user identity information, it very likely has access to all of them), so you should set "required" and "optional" eligibility requirements based on your true business needs.`}</p>
        <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
          <p parentName="li">{`For a detailed tour of the available Layer template configuration options, see the `}<a parentName="p" {...{
              "href": "https://www.youtube.com/watch?v=5vYFAsfEaqM"
            }}>{`Layer Dashboard video walkthrough`}</a>{`.`}</p>
        </Callout>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/session/token/create`}</inlineCode>{` with the template ID for the Layer template you configured. This will return a `}<inlineCode parentName="p">{`link`}</inlineCode>{` object that contains a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On your client, create an instance of Link using the `}<inlineCode parentName="p">{`link.link_token`}</inlineCode>{` string returned by `}<inlineCode parentName="p">{`/session/token/create`}</inlineCode>{`.
With Layer, you should create the Plaid handler or session object as early as possible so Link
can pre-load in the background. This ensures the fastest possible experience for your users.`}</p>
        <p parentName="li">{`  If you have an existing Android, React Native, or Flutter integration, make sure you are on a Layer-capable SDK version and are using the current session-based APIs to open Link. For details and sample code, see `}<a parentName="p" {...{
            "href": "/docs/link/react-native/#opening-link"
          }}>{`React Native: opening Link`}</a>{`, `}<a parentName="p" {...{
            "href": "/docs/link/flutter/#opening-link"
          }}>{`Flutter: opening Link`}</a>{`, and `}<a parentName="p" {...{
            "href": "/docs/link/android/#opening-link"
          }}>{`Android: opening Link`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once you have a user's phone number, submit it by calling the `}<inlineCode parentName="p">{`submit`}</inlineCode>{` method on the handler or session you created earlier.
Link will quickly check if the user is eligible for Layer, and call the Link `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callback with either `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{` or
`}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{`. Note that if the phone number uses a country code other than +1, the result will always be `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{`, as Layer does not support international phone numbers. A user's phone number is eligible only if the user previously chose to be `}<a parentName="p" {...{
            "href": "/docs/link/returning-user/"
          }}>{`remembered by Plaid`}</a>{` during a Link session; Layer uses the phone number to look up that saved profile rather than matching accounts based on identity data you submit. Users whose phone number is not eligible may still qualify via `}<a parentName="p" {...{
            "href": "#extended-autofill"
          }}>{`Extended Autofill`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When you receive `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{`, it means the user is eligible and you should call `}<inlineCode parentName="p">{`handler.open()`}</inlineCode>{` to immediately present the Layer UI.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional, recommended) If you receive `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{` instead, collect the user's date of birth and `}<inlineCode parentName="p">{`submit`}</inlineCode>{` it via the same handler as earlier, e.g. `}<inlineCode parentName="p">{`handler.submit({ "date_of_birth": "1975-01-18"})`}</inlineCode>{`. If you receive `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{`, the user is eligible for `}<a parentName="p" {...{
            "href": "#extended-autofill"
          }}>{`Layer Extended Autofill`}</a>{` and you should call `}<inlineCode parentName="p">{`handler.open()`}</inlineCode>{` to immediately present the Layer UI; the flow continues at step 8. If you receive `}<inlineCode parentName="p">{`LAYER_AUTOFILL_NOT_AVAILABLE`}</inlineCode>{`, the flow continues with the next step.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If neither Layer nor Layer Extended Autofill are available, you may discard the handler object and continue with any non-Layer flow in your app. For example, if your application falls back to a non-Layer product flow, you'd create a new Link token and handler to present that flow. If your non-Layer flow involves creating a Link token, include the `}<inlineCode parentName="p">{`phone_number`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` for a faster end user experience.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`After you have launched the Layer flow in Link via `}<inlineCode parentName="p">{`handler.open()`}</inlineCode>{`, the end user enters the Layer flow. You will receive a `}<a parentName="p" {...{
            "href": "/docs/api/products/layer/#layer_authentication_passed"
          }}><inlineCode parentName="a">{`LAYER_AUTHENTICATION_PASSED`}</inlineCode></a>{` webhook once Plaid has determined that the end user has demonstrated ownership of the phone number, via either OTP or SNA. If the end user does not fully complete the Link flow but you receive this webhook, you can skip any OTP phone number verification steps you would otherwise implement.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the end user has successfully completed the Layer flow in Link, you will receive an `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback and a `}<a parentName="p" {...{
            "href": "/docs/api/products/layer/#session_finished"
          }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook, both of which contain the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`. Use the received `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` with the `}<inlineCode parentName="p">{`/user_account/session/get`}</inlineCode>{` API in order to retrieve the user's profile information and any linked Items.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "sample-code"
    }}>{`Sample code`}</h4>
    <p>{`For a walkthrough of integrating Layer, including sample code across multiple platforms, see `}<a parentName="p" {...{
        "href": "/docs/layer/add-to-app"
      }}>{`Add Layer to your app`}</a>{`.`}</p>
    <h4 {...{
      "id": "minimum-sdk-versions"
    }}>{`Minimum SDK versions`}</h4>
    <p>{`As Plaid is actively adding new Layer functionality, it is strongly recommended that you use the latest SDK version and keep up to date with SDK updates if your app uses Layer.`}</p>
    <p>{`If you are developing for the web using the JavaScript SDK, Plaid will automatically provide the latest available SDK. Otherwise, see `}<a parentName="p" {...{
        "href": "/docs/api/libraries/#link-client-sdks"
      }}>{`libraries`}</a>{` for links to the latest SDKs.`}</p>
    <p>{`If you are developing a native mobile application, minimum SDK versions for Layer are 6.0.4 (iOS), 4.5.0 (Android), 11.11.0 (React Native), 1.0.0 (Flutter), and 3.5.2 (React). For Extended Autofill support, minimum versions are 6.3.1 (iOS), 5.3.0 (Android), 12.4.0 (React Native), 1.0.0 (Flutter), and 4.1.1 (React).`}</p>
    <p>{`Layer is not compatible with the `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link/"
      }}>{`Hosted Link`}</a>{` or `}<a parentName="p" {...{
        "href": "/docs/link/webview/"
      }}>{`webview`}</a>{` integration modes.`}</p>
    <h4 {...{
      "id": "supported-languages"
    }}>{`Supported languages`}</h4>
    <p>{`Layer is available in English, Spanish, French, Portuguese, Polish, and Vietnamese. The display language is selected automatically based on the user's device language settings and cannot be set programmatically.`}</p>
    <h4 {...{
      "id": "extended-autofill"
    }}>{`Extended Autofill`}</h4>
    <p>{`Extended Autofill allows you to use the user's date of birth as a fallback for autofilling their identity data if their phone number is not Layer eligible. For Extended Autofill support, minimum SDK versions are 6.3.1 (iOS), 5.3.0 (Android), 12.4.0 (React Native), 1.0.0 (Flutter), and 4.1.1 (React).`}</p>
    <p>{`To use Extended Autofill, after receiving `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{`, submit the date of birth, e.g. `}<inlineCode parentName="p">{`handler.submit({ "date_of_birth": "1975-01-18"})`}</inlineCode>{`. This must be in a separate call, made only after calling `}<inlineCode parentName="p">{`submit`}</inlineCode>{` with the user's phone number. You will get either a `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{` callback (success) or a `}<inlineCode parentName="p">{`LAYER_AUTOFILL_NOT_AVAILABLE`}</inlineCode>{` callback (failure).`}</p>
    <p>{`Extended Autofill is still subject to the same eligibility requirements that you defined in your Layer template. You may wish to make `}<strong parentName="p">{`Collect financial accounts`}</strong>{` "Optional" instead of "Required" in your eligibility requirements, as Extended Autofill users are less likely to have connected account information.`}</p>
    <p>{`When using Extended Autofill, in order to reduce onboarding friction, it is strongly recommended to prompt the user for their date of birth only after receiving `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{`, rather than requesting it upfront.`}</p>
    <h4 {...{
      "id": "intelligent-account-sorting"
    }}>{`Intelligent Account Sorting`}</h4>
    <p>{` By default, Layer orders accounts in Link based on which one is likeliest to have the highest conversion. With Intelligent Account Sorting, you can instead prioritize either the highest-balance account or the account that Plaid detects as being the user's primary bank account. The prioritized account will have a "Recommended" badge displayed next to it in Link. Intelligent Account Sorting may be a good fit for customers who are using Layer in flows for cashflow underwriting, EWA, or cash advance.`}</p>
    <p>{` Intelligent Account Sorting settings can be configured in your Layer template via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/layer"
      }}>{`Dashboard`}</a>{`.`}</p>
    <h4 {...{
      "id": "testing-layer-in-sandbox"
    }}>{`Testing Layer in Sandbox`}</h4>
    <p>{`Because Layer looks up a saved profile from the user's phone number, testing in Sandbox requires using a profile.`}</p>
    <h5 {...{
      "id": "hard-coded-profiles"
    }}>{`Hard-coded profiles`}</h5>
    <p>{`The phone numbers below have hard-coded profiles, meaning they match to seeded users that trigger various useful test scenarios, but cannot be modified. `}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Phone number`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Notes`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`415-555-0011`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Two connected banks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`415-555-0012`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing PII; three connected banks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`415-555-0015`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`One connected bank`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0013`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing email`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0015`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing DOB`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0016`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing SSN`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0017`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing address`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0018`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Missing name`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`515-555-0019`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Savings account only`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`In Sandbox, the authentication code for all phone numbers is `}<inlineCode parentName="p">{`123456`}</inlineCode>{`.`}</p>
    <p>{`Note the area code is not the same in all of these test numbers. Unlike Sandbox Profiles, these numbers are shared between Plaid teams and cannot be reset or extended with new institutions. For profiles that can grow with new institutions over time, see the starter profiles in `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/#starter-profiles"
      }}>{`Sandbox Profiles`}</a>{`.`}</p>
    <h5 {...{
      "id": "editable-sandbox-profiles"
    }}>{`Editable Sandbox Profiles`}</h5>
    <p>{`In addition to the hard-coded profiles above, you can also use Sandbox Profiles. Using any number (except certain hard-coded test numbers) within Link on Sandbox will create a profile for that user that can be re-used in Sandbox later. `}</p>
    <p>{`The numbers `}<inlineCode parentName="p">{`415-555-0131`}</inlineCode>{` through `}<inlineCode parentName="p">{`415-555-0136`}</inlineCode>{` are also pre-populated with profile data that can be used for Layer testing. `}</p>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles"
      }}>{`Sandbox Profiles`}</a>{`.`}</p>
    <h5 {...{
      "id": "device-authentication-in-sandbox"
    }}>{`Device authentication in Sandbox`}</h5>
    <p>{`In Production, the authentication step of Layer is dynamic and may authenticate the user via SNA or SMS OTP. In Sandbox, device-level checks such as SNA, trusted device matching, and SIM swap detection cannot be run, and users can only be authenticated via OTP.`}</p>
    <h5 {...{
      "id": "testing-extended-autofill-in-sandbox"
    }}>{`Testing Extended Autofill in Sandbox`}</h5>
    <p>{`You can trigger Extended Autofill in Sandbox in one of two ways:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Submit a hard-coded profile phone number that is missing some identity information (such as `}<inlineCode parentName="p">{`515-555-0017`}</inlineCode>{`) while using a Layer template that requires that information. `}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Or, submit any phone number that has no associated profile, outside of Plaid's `}<inlineCode parentName="p">{`415-555-****`}</inlineCode>{` test range, with a template that specifies `}<strong parentName="p">{`Collect financial accounts`}</strong>{` as Optional or Skip. Make sure to use a number that has not been used in Link before: completing a Link session with a phone number in Sandbox creates a `}<a parentName="p" {...{
            "href": "/docs/sandbox/profiles/"
          }}>{`Sandbox Profile`}</a>{` for it, after which it will be Layer-eligible.`}</p>
      </li>
    </ul>
    <p>{`In both cases, submit `}<inlineCode parentName="p">{`1975-01-18`}</inlineCode>{` for the user's date of birth.`}</p>
    <h4 {...{
      "id": "layer-webhooks"
    }}>{`Layer webhooks`}</h4>
    <p>{`Layer has the following webhooks:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Event`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/api/products/layer/#layer_authentication_passed"
            }}><inlineCode parentName="a">{`LAYER_AUTHENTICATION_PASSED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A user has been authenticated`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/api/products/layer/#session_finished"
            }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A Layer session has finished`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "layer-events"
    }}>{`Layer events`}</h4>
    <p>{`In addition to the standard Link events, Layer has the following events:`}</p>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Event`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Meaning`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LAYER_READY`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid found data matching template requirements; call `}<inlineCode parentName="td">{`handler.open()`}</inlineCode>{` to present Layer`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LAYER_NOT_AVAILABLE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No data found matching template requirements based on the phone number; prompt for date of birth or use non-Layer flow`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`LAYER_AUTOFILL_NOT_AVAILABLE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No data found matching template requirements based on the phone number + date of birth; use non-Layer flow`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SUBMIT_OTP`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`User has entered an OTP code to verify their phone number (also used by Returning User Experience)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`VERIFY_PHONE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`User has successfully verified their phone number (also used by Returning User Experience)`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <p>{`For a full list of events, see the `}<a parentName="p" {...{
        "href": "/docs/link/web/#onevent"
      }}>{`Link onEvent documentation`}</a>{`.`}</p>
    <h5 {...{
      "id": "example-event-sequences"
    }}>{`Example event sequences`}</h5>
    <p>{`The sections below illustrate typical frontend event sequences for different Layer scenarios. For more details on frontend events, see the `}<a parentName="p" {...{
        "href": "/docs/link/web#onevent"
      }}>{`Link onEvent documentation`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Events for successful Layer flow, verified via SNA",
        "switcher": "false",
        "#Events": true,
        "for": true,
        "successful": true,
        "Layer": true,
        "flow,": true,
        "verified": true,
        "via": true,
        "SNA": true
      }}>{`LAYER_READY
OPEN
TRANSITION_VIEW: CONSENT
TRANSITION_VIEW: VERIFY_PHONE
TRANSITION_VIEW: PROFILE_DATA_REVIEW
VERIFY_PHONE
HANDOFF (with onSuccess callback)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Events for failed Layer flow, end user entered OTP incorrectly three times",
        "#Events": true,
        "for": true,
        "failed": true,
        "Layer": true,
        "flow,": true,
        "end": true,
        "user": true,
        "entered": true,
        "OTP": true,
        "incorrectly": true,
        "three": true,
        "times": true
      }}>{`LAYER_READY
OPEN
TRANSITION_VIEW: CONSENT
TRANSITION_VIEW: VERIFY_PHONE
SUBMIT_OTP
ERROR: INVALID_OTP
SUBMIT_OTP
ERROR: INVALID_OTP
SUBMIT_OTP
TRANSITION_VIEW: ERROR
ERROR: PROFILE_AUTHENTICATION_FAILED
EXIT (with onExit callback)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Events for Layer-ineligible flow",
        "#Events": true,
        "for": true,
        "Layer-ineligible": true,
        "flow": true
      }}>{`LAYER_NOT_AVAILABLE
LAYER_AUTOFILL_NOT_AVAILABLE
`}</code></pre>
    <h4 {...{
      "id": "measuring-conversion"
    }}>{`Measuring conversion`}</h4>
    <p>{`To track Layer performance, measure the following ratios:`}</p>
    <p><strong parentName="p">{`Layer eligibility percentage`}</strong>{`: `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{` event count / `}<inlineCode parentName="p">{`handler.submit(phone_number)`}</inlineCode>{` count. Ensure you do not count `}<inlineCode parentName="p">{`handler.submit`}</inlineCode>{` calls for date of birth.`}</p>
    <p><strong parentName="p">{`Layer conversion`}</strong>{`: `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback count (or `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` event count) / `}<inlineCode parentName="p">{`OPEN`}</inlineCode>{` event count`}</p>
    <h4 {...{
      "id": "billing"
    }}>{`Billing`}</h4>
    <p>{`When using Layer, a billing event is incurred for each converted Link session (when `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` fires). You will not be billed for Layer eligibility checks or unconverted Layer sessions.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "sample-code",
        level: 4,
        title: "Sample code",
      },
{
        id: "minimum-sdk-versions",
        level: 4,
        title: "Minimum SDK versions",
      },
{
        id: "supported-languages",
        level: 4,
        title: "Supported languages",
      },
{
        id: "extended-autofill",
        level: 4,
        title: "Extended Autofill",
      },
{
        id: "intelligent-account-sorting",
        level: 4,
        title: "Intelligent Account Sorting",
      },
{
        id: "testing-layer-in-sandbox",
        level: 4,
        title: "Testing Layer in Sandbox",
      },
{
        id: "layer-webhooks",
        level: 4,
        title: "Layer webhooks",
      },
{
        id: "layer-events",
        level: 4,
        title: "Layer events",
      },
{
        id: "measuring-conversion",
        level: 4,
        title: "Measuring conversion",
      },
{
        id: "billing",
        level: 4,
        title: "Billing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
